import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalPanel,
} from '../Common/ModalShell';
import {
  audienceLabel,
  createMembershipTier,
  MEMBERSHIP_AUDIENCES,
  updateMembershipTier,
  type MembershipAudience,
  type MembershipTierRow,
} from '../../lib/membershipTiers';

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

const labelClass = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

const SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function MembershipTierModal({
  isOpen,
  onClose,
  tier,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  tier: MembershipTierRow | null;
  onSuccess: () => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [audience, setAudience] = useState<MembershipAudience>('individual');
  const [priceLabel, setPriceLabel] = useState('');
  const [priceHint, setPriceHint] = useState('');
  const [stripePriceId, setStripePriceId] = useState('');
  const [stripePriceIdTest, setStripePriceIdTest] = useState('');
  const [customMin, setCustomMin] = useState('');
  const [customDefault, setCustomDefault] = useState('');
  const [customSuffix, setCustomSuffix] = useState('');
  const [isFree, setIsFree] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const [monthlyCallLimit, setMonthlyCallLimit] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (tier) {
      setSlug(tier.slug);
      setName(tier.name);
      setAudience(tier.audience);
      setPriceLabel(tier.priceLabel);
      setPriceHint(tier.priceHint ?? '');
      setStripePriceId(tier.stripePriceId ?? '');
      setStripePriceIdTest(tier.stripePriceIdTest ?? '');
      setCustomMin(tier.customAmountMin === null ? '' : String(tier.customAmountMin));
      setCustomDefault(tier.customAmountDefault === null ? '' : String(tier.customAmountDefault));
      setCustomSuffix(tier.customAmountSuffix ?? '');
      setIsFree(tier.isFree);
      setFeatured(tier.featured);
      setActive(tier.active);
      setSortOrder(tier.sortOrder);
      setMonthlyCallLimit(
        tier.monthlyCallLimit === null || tier.monthlyCallLimit === undefined
          ? ''
          : String(tier.monthlyCallLimit),
      );
    } else {
      setSlug('');
      setName('');
      setAudience('individual');
      setPriceLabel('');
      setPriceHint('');
      setStripePriceId('');
      setStripePriceIdTest('');
      setCustomMin('');
      setCustomDefault('');
      setCustomSuffix('');
      setIsFree(false);
      setFeatured(false);
      setActive(true);
      setSortOrder(0);
      setMonthlyCallLimit('');
    }
  }, [isOpen, tier]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }

    const normalizedSlug = slug.trim() || slugify(trimmedName);
    if (!SLUG_RE.test(normalizedSlug)) {
      toast.error('Slug must be lowercase letters, digits, hyphens or underscores');
      return;
    }

    const min = customMin.trim() === '' ? null : Number(customMin);
    const def = customDefault.trim() === '' ? null : Number(customDefault);
    if (!isFree && (min === null) !== (def === null)) {
      toast.error('Custom amount needs both a minimum and a default, or neither');
      return;
    }
    if (min !== null && def !== null) {
      if (!Number.isFinite(min) || !Number.isFinite(def) || min <= 0) {
        toast.error('Custom amount must be a positive number');
        return;
      }
      if (def < min) {
        toast.error('Custom default cannot be below the minimum');
        return;
      }
    }
    if (!isFree && !stripePriceId.trim() && min === null) {
      // Not fatal — a tier can be drafted before the Stripe product exists. The list
      // flags it, and checkout has to refuse it. A test price does not lift this: it
      // sells nothing in production.
      toast.warning('Saved without a live Stripe price — this tier cannot take real payment yet');
    }

    const callLimit =
      monthlyCallLimit.trim() === '' ? null : Number(monthlyCallLimit.trim());
    if (callLimit !== null && (!Number.isFinite(callLimit) || callLimit < 0 || !Number.isInteger(callLimit))) {
      toast.error('Monthly call limit must be a whole number, or blank for unlimited');
      return;
    }

    const input = {
      slug: normalizedSlug,
      audience,
      name: trimmedName,
      priceLabel: priceLabel.trim(),
      priceHint: priceHint.trim() || null,
      stripePriceId: stripePriceId.trim() || null,
      stripePriceIdTest: stripePriceIdTest.trim() || null,
      customAmountMin: min,
      customAmountDefault: def,
      customAmountSuffix: customSuffix.trim() || null,
      isFree,
      featured,
      active,
      sortOrder,
      monthlyCallLimit: callLimit,
    };

    setSaving(true);
    try {
      if (tier) {
        await updateMembershipTier(tier.id, input);
        toast.success('Tier updated');
      } else {
        await createMembershipTier(input);
        toast.success('Tier created');
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClose={onClose}>
      <ModalPanel maxWidthClass="max-w-2xl" titleId="tier-modal-title">
        <ModalHeader
          title={tier ? 'Edit tier' : 'New tier'}
          titleId="tier-modal-title"
          onClose={onClose}
          description="Shown on members.stephansmith.me/upgrade."
        />

        <ModalBody className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="tier-name">
                Name
              </label>
              <input
                id="tier-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                  if (!tier && name.trim() && !slug.trim()) setSlug(slugify(name));
                }}
                className={inputClass}
                placeholder="Monthly"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="tier-slug">
                Slug
              </label>
              <input
                id="tier-slug"
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                disabled={Boolean(tier)}
                className={`${inputClass} font-mono disabled:opacity-60 disabled:cursor-not-allowed`}
                placeholder="monthly"
              />
              {tier ? (
                <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                  Fixed — app code and Stripe metadata key off this.
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="tier-audience">
                Audience
              </label>
              <select
                id="tier-audience"
                value={audience}
                onChange={(e) => setAudience(e.target.value as MembershipAudience)}
                className={inputClass}
              >
                {MEMBERSHIP_AUDIENCES.map((a) => (
                  <option key={a} value={a}>
                    {audienceLabel(a)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="tier-sort">
                Sort order
              </label>
              <input
                id="tier-sort"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="tier-price-label">
                Price label
              </label>
              <input
                id="tier-price-label"
                type="text"
                value={priceLabel}
                onChange={(e) => setPriceLabel(e.target.value)}
                className={inputClass}
                placeholder="$7/month"
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="tier-price-hint">
                Price hint
              </label>
              <input
                id="tier-price-hint"
                type="text"
                value={priceHint}
                onChange={(e) => setPriceHint(e.target.value)}
                className={inputClass}
                placeholder="$5.83/month · 17% less than monthly"
              />
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={isFree}
                onChange={(e) => setIsFree(e.target.checked)}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              Free tier — included with a newsletter subscription
            </label>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Only one free tier per audience. A free tier cannot hold a Stripe price, so those
              fields are cleared on save.
            </p>
          </div>

          <div>
            <label className={labelClass} htmlFor="tier-monthly-calls">
              Monthly call limit
            </label>
            <input
              id="tier-monthly-calls"
              type="number"
              min={0}
              step={1}
              value={monthlyCallLimit}
              onChange={(e) => setMonthlyCallLimit(e.target.value)}
              className={inputClass}
              placeholder="Blank = unlimited"
            />
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Tool calls per calendar month for My AI CTO. Leave blank for unlimited.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass} htmlFor="tier-stripe">
                Stripe price ID — live
              </label>
              <input
                id="tier-stripe"
                type="text"
                value={stripePriceId}
                onChange={(e) => setStripePriceId(e.target.value)}
                disabled={isFree}
                className={`${inputClass} font-mono disabled:opacity-60 disabled:cursor-not-allowed`}
                placeholder="price_1AbC…"
              />
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Paste from Stripe once the live product exists. Empty means this tier cannot take
                real payment.
              </p>
            </div>
            <div>
              <label className={labelClass} htmlFor="tier-stripe-test">
                Stripe price ID — test
              </label>
              <input
                id="tier-stripe-test"
                type="text"
                value={stripePriceIdTest}
                onChange={(e) => setStripePriceIdTest(e.target.value)}
                disabled={isFree}
                className={`${inputClass} font-mono disabled:opacity-60 disabled:cursor-not-allowed`}
                placeholder="price_1AbC…"
              />
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Sandbox price. Only reached by deployments running in test mode, which can never
                grant real access.
              </p>
            </div>
          </div>

          <fieldset
            disabled={isFree}
            className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 disabled:opacity-60"
          >
            <legend className="px-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              Pay what you want (founding)
            </legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className={labelClass} htmlFor="tier-custom-min">
                  Minimum
                </label>
                <input
                  id="tier-custom-min"
                  type="number"
                  min={1}
                  value={customMin}
                  onChange={(e) => setCustomMin(e.target.value)}
                  className={inputClass}
                  placeholder="70"
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="tier-custom-default">
                  Default
                </label>
                <input
                  id="tier-custom-default"
                  type="number"
                  min={1}
                  value={customDefault}
                  onChange={(e) => setCustomDefault(e.target.value)}
                  className={inputClass}
                  placeholder="210"
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="tier-custom-suffix">
                  Suffix
                </label>
                <input
                  id="tier-custom-suffix"
                  type="text"
                  value={customSuffix}
                  onChange={(e) => setCustomSuffix(e.target.value)}
                  className={inputClass}
                  placeholder="/ year"
                />
              </div>
            </div>
            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
              Leave all three empty for a fixed-price tier.
            </p>
          </fieldset>

          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              Featured — highlighted card
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              Active — visible on the upgrade page
            </label>
          </div>
        </ModalBody>

        <ModalFooter className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
}
